import { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { GlobalNavbar } from '../components/global-navbar';
import { OptInSection } from '../components/OptInSection';
import { useSeo } from '../utils/useSeo';

const solutionPoints = [
  {
    benefit: 'Alerts on underpriced listings, not just new listings.',
    detail:
      'The system compares asking price against market value for the same make, model, year, and mileage and only flags cars where the gap is real.',
  },
  {
    benefit: 'Set your exact buying criteria.',
    detail:
      'Filter by make, model, price range, mileage, year, and location. You see only the inventory that fits the cars you actually flip.',
  },
  {
    benefit: 'Move first. Every time.',
    detail:
      "Early alerts mean you're calling the seller while other dealers are still finding the listing. Speed is margin.",
  },
  {
    benefit: 'Built for Mobile.de - the biggest used car market in the EU.',
    detail:
      'Deals Finder runs directly against Mobile.de inventory, where the volume of listings is highest and the sourcing competition is toughest.',
  },
  {
    benefit: 'No more wasted hours on manual searches.',
    detail:
      'What used to take 2-3 hours of daily scrolling is replaced by a feed of pre-qualified opportunities waiting for you when you open the app.',
  },
] as const;

const howItWorksSteps = [
  {
    description:
      "Tell Profit Cars what you're looking for: make, model, year range, max mileage, and price ceiling. These become your deal filters.",
    title: 'Step 1 - Set your criteria',
  },
  {
    description:
      "The system scans Mobile.de continuously, comparing each new listing's price against market benchmarks for that exact vehicle type.",
    title: 'Step 2 - Deals Finder monitors Mobile.de',
  },
  {
    description:
      'When a car is listed below market value and matches your profile, you get a notification. You review the deal, not the noise.',
    title: 'Step 3 - You get alerted on real opportunities',
  },
  {
    description:
      'You already know the margin before you call. You know what the car is worth. You act decisively because the numbers are clear.',
    title: 'Step 4 - You move fast and buy with confidence',
  },
] as const;

const faqs = [
  {
    answer:
      'Deals Finder currently monitors Mobile.de, the largest used car marketplace in the EU by listing volume. This is where competition is highest and where having an automated edge matters most.',
    question: 'What platforms does Deals Finder monitor?',
  },
  {
    answer:
      'The system compares each listing price to market benchmarks for the same make, model, year, and mileage tier. When a car is priced significantly below comparable listings, it gets flagged as a deal opportunity.',
    question: 'How does it know a car is underpriced?',
  },
  {
    answer:
      'Yes. You define your criteria including make, model, price range, mileage, and more. You only see listings that match your buying profile.',
    question: 'Can I set my own filters?',
  },
  {
    answer:
      "No. If anything, low-to-mid volume dealers benefit most. When every deal matters, you cannot afford to miss the good ones or overpay because you did not have time to compare properly.",
    question: 'Is this only useful for high-volume dealers?',
  },
  {
    answer:
      'Not necessarily, but it gives you a parallel sourcing channel that runs 24/7 without you having to be present. Many dealers use Deals Finder to supplement auction buying.',
    question: 'Does this replace going to auctions?',
  },
] as const;

export function DealsFinderPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const handleScrollToOptIn = () => {
    document.getElementById('optin-get')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  useSeo({
    description:
      "Profit Cars' Deals Finder monitors Mobile.de 24/7 and alerts you the moment an underpriced car matches your criteria. Stop wasting hours on manual searches. Source smarter, buy faster, flip bigger margins.",
    image: '/deals-finder.png',
    keywords: [
      'deals finder',
      'mobile.de monitor',
      'underpriced cars',
      'car sourcing tool',
      'used car dealer software',
    ],
    path: '/features/deals-finder',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      description:
        "Profit Cars' Deals Finder monitors Mobile.de and alerts EU dealers when underpriced listings match their buying criteria.",
      name: 'Deals Finder - Find Underpriced Cars Before Other Dealers',
      url: '/features/deals-finder',
    },
    title: 'Deals Finder - Find Underpriced Cars Before Other Dealers | Profit Cars',
    type: 'website',
  });

  return (
    <main className="min-h-screen bg-[#ececec] text-[#111827]">
      <GlobalNavbar />

      <section className="border-b border-black/10 bg-[#e6e7ea]">
        <div className="mx-auto grid w-full max-w-6xl gap-10 px-6 pb-14 pt-14 lg:grid-cols-[1fr_0.9fr] lg:items-center lg:px-8">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#2f5b1b]">Deals Finder</p>
            <h1 className="mt-4 max-w-4xl font-display text-5xl leading-[0.94] text-black sm:text-6xl">
              Find Underpriced Cars Before Your Competition Does.
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg">
              Deals Finder monitors Mobile.de around the clock and surfaces the deals worth acting on so you stop
              scrolling and start buying.
            </p>

            <div className="mt-8">
              <button
                className="inline-flex h-12 items-center justify-center rounded-[10px] border border-black/65 bg-[#08090c] px-7 text-sm font-bold text-white transition hover:border-white/35 hover:bg-[#141820]"
                onClick={handleScrollToOptIn}
                type="button"
              >
                Get Access to Profit Cars
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>

          <img alt="Deals Finder feature preview" className="h-auto w-full" src="/deals-finder.png" />
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <h2 className="max-w-5xl text-3xl font-extrabold leading-tight text-black sm:text-4xl">
            Every hour you spend searching is an hour another dealer already bought the car.
          </h2>
          <div className="mt-6 max-w-5xl space-y-5 text-base leading-relaxed text-slate-700 sm:text-lg">
            <p>
              The EU used car market moves fast. The best deals, the ones that actually have margin in them, disappear
              in hours. Most dealers still find them the same way: open Mobile.de, set a filter, scroll, compare,
              guess.
            </p>
            <p>That is not a sourcing strategy. That is a lottery.</p>
            <p>
              By the time you have manually checked listings for BMW 3 Series, Volkswagen Passat, and Mercedes C-Class
              across three search tabs, the underpriced E-Class you were looking for is already sold to the dealer who
              had a system.
            </p>
          </div>
        </div>
      </section>

      <section className="border-y border-black/10 bg-[#f1f2f4] py-14 sm:py-16">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <h2 className="max-w-4xl text-3xl font-extrabold leading-tight text-black sm:text-4xl">
            Deals Finder is your always-on sourcing system.
          </h2>
          <p className="mt-5 max-w-5xl text-base leading-relaxed text-slate-700 sm:text-lg">
            Set your criteria once. Deals Finder monitors Mobile.de continuously and alerts you the moment a car that
            matches your buying profile is listed below market price. No scrolling. No guessing. Just deals that are
            actually worth acting on.
          </p>

          <ul className="mt-8 grid gap-4 md:grid-cols-2">
            {solutionPoints.map((item) => (
              <li className="rounded-[16px] border border-black/10 bg-white p-5" key={item.benefit}>
                <p className="text-base font-extrabold text-black">{item.benefit}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700 sm:text-base">{item.detail}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold leading-tight text-black sm:text-4xl">How Deals Finder Works</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {howItWorksSteps.map((step) => (
              <article className="rounded-[16px] border border-black/10 bg-white p-5" key={step.title}>
                <h3 className="text-lg font-extrabold leading-tight text-black">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">{step.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-black/10 bg-[#f1f2f4] py-14 sm:py-16">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <h2 className="max-w-5xl text-3xl font-extrabold leading-tight text-black sm:text-4xl">
            Why EU Car Dealers Need a Smarter Sourcing Tool in 2026
          </h2>
          <div className="mt-6 max-w-5xl space-y-6 text-base leading-relaxed text-slate-700 sm:text-lg">
            <p>
              The European used car market is one of the most competitive in the world. Germany alone sees hundreds of
              thousands of used car transactions every month, and platforms like Mobile.de list millions of vehicles
              at any given time. For independent dealers and car flippers working without the resources of large dealer
              groups, finding genuinely underpriced inventory has always come down to who is watching the hardest.
            </p>
            <p>That era is over.</p>

            <div>
              <h3 className="text-xl font-extrabold text-black sm:text-2xl">The problem with manual sourcing</h3>
              <p className="mt-2">
                Independent dealers who source manually spend an average of 2-3 hours per day checking listing
                platforms. They set saved searches, refresh feeds, and check prices against rough memory of what a car
                should cost. The result is inconsistent: sometimes you catch a deal, most days you do not.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-extrabold text-black sm:text-2xl">What an automated deal finder changes</h3>
              <p className="mt-2">
                A car deal monitoring tool like Profit Cars Deals Finder removes dependency on manual effort. Instead
                of you going to the market, the market comes to you, filtered, pre-qualified, and ranked by actual
                margin opportunity. For EU dealers focused on Mobile.de, this is the difference between reactive
                sourcing and a repeatable system.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-extrabold text-black sm:text-2xl">Who this is built for</h3>
              <p className="mt-2">
                Deals Finder is designed for independent EU car dealers, car flippers, and small dealerships who buy
                and resell used cars on platforms like Mobile.de. If you are flipping 3-20 cars a month and your
                biggest bottleneck is finding inventory with real margin, this tool directly addresses that problem.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold leading-tight text-black sm:text-4xl">
            Dealers using Deals Finder buy better cars, faster.
          </h2>
          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            <blockquote className="rounded-[16px] border border-black/10 bg-white p-6">
              <p className="text-base leading-relaxed text-slate-800">
                "Deals Finder alone makes this worth it. I see underpriced cars before other dealers in my area and
                can move on them immediately. My sourcing is much faster now."
              </p>
              <footer className="mt-4 text-sm font-semibold text-slate-600">
                Carlos M., Independent Dealer - Madrid
              </footer>
            </blockquote>
            <blockquote className="rounded-[16px] border border-black/10 bg-white p-6">
              <p className="text-base leading-relaxed text-slate-800">
                "Before Profit Cars we wasted hours checking listings manually. Now the system surfaces the deals that
                actually matter. We buy better cars and waste less time."
              </p>
              <footer className="mt-4 text-sm font-semibold text-slate-600">
                Lukas H., Used Car Buyer - Berlin
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      <section className="border-y border-black/10 bg-[#f1f2f4] py-14 sm:py-16">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold leading-tight text-black sm:text-4xl">Questions about Deals Finder</h2>
          <div className="mt-8 space-y-3">
            {faqs.map((faq) => (
              <article className="rounded-[14px] border border-black/10 bg-white p-5" key={faq.question}>
                <h3 className="text-lg font-extrabold leading-tight text-black">{faq.question}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-700 sm:text-base">{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <OptInSection className="py-16 sm:py-20" id="optin-get" source="final_cta" />
    </main>
  );
}
